import * as React from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Typography, Box,
    TextField, Button, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UserFetch, { UserFetchJson } from '../../userFetch';
import Photo from '../../models/photo';

// Cap the LONGEST side, not the width: capping width alone let a portrait photo
// through at 2000x2667 (5.3 MP) while a landscape one became 2000x1500 (3.0 MP),
// so the same byte budget had to cover 78% more pixels — portraits came out
// visibly grainy and posterized (Norm, 2026-07-30).
const MAX_DIMENSION = 2000;
const MAX_FILE_SIZE = 400 * 1024; // 400KB target
const MIN_QUALITY = 0.45; // below this JPEG blocking is worse than a bigger file

interface PhotosDialogProps {
    open: boolean;
    onClose: () => void;
    serialNumber: string;
    deviceLabel: string;
    isSuperAdmin: boolean;
    onCountChange?: (count: number) => void;
}

async function resizeImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            const longest = Math.max(width, height);
            if (longest > MAX_DIMENSION) {
                const ratio = MAX_DIMENSION / longest;
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('Canvas not supported')); return; }
            ctx.drawImage(img, 0, 0, width, height);

            let quality = 0.7;
            const tryCompress = () => {
                canvas.toBlob((blob) => {
                    if (!blob) { reject(new Error('Compression failed')); return; }
                    if (blob.size > MAX_FILE_SIZE && quality > MIN_QUALITY) {
                        quality -= 0.1;
                        tryCompress();
                    } else {
                        resolve(blob);
                    }
                }, 'image/jpeg', quality);
            };
            tryCompress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
    });
}

function PhotoLightbox({ photo, onClose }: { photo: Photo; onClose: () => void }) {
    const [scale, setScale] = React.useState(1);
    const [translate, setTranslate] = React.useState({ x: 0, y: 0 });
    const [dragging, setDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const [lastPinchDist, setLastPinchDist] = React.useState(0);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const resetZoom = () => { setScale(1); setTranslate({ x: 0, y: 0 }); };

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        setScale(prev => Math.min(5, Math.max(1, prev + delta)));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale <= 1) return;
        e.preventDefault();
        setDragging(true);
        setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!dragging) return;
        setTranslate({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };

    const handleMouseUp = () => setDragging(false);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            setLastPinchDist(dist);
        } else if (e.touches.length === 1 && scale > 1) {
            setDragging(true);
            setDragStart({ x: e.touches[0].clientX - translate.x, y: e.touches[0].clientY - translate.y });
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 2 && lastPinchDist > 0) {
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const delta = (dist - lastPinchDist) * 0.01;
            setScale(prev => Math.min(5, Math.max(1, prev + delta)));
            setLastPinchDist(dist);
        } else if (e.touches.length === 1 && dragging) {
            setTranslate({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
        }
    };

    const handleTouchEnd = () => { setDragging(false); setLastPinchDist(0); };

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}
            PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {photo.filename}{photo.description ? ` — ${photo.description}` : ''}
                </Typography>
                <Box>
                    <Button size="small" onClick={resetZoom} sx={{ color: 'text.secondary', mr: 1 }}>Reset</Button>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'default' }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={resetZoom}
            >
                <img
                    src={photo.url}
                    alt={photo.filename}
                    draggable={false}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '85vh',
                        objectFit: 'contain',
                        transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
                        transition: dragging ? 'none' : 'transform 0.1s ease-out',
                        userSelect: 'none',
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function PhotosDialog({ open, onClose, serialNumber, deviceLabel, isSuperAdmin, onCountChange }: PhotosDialogProps) {
    const [photos, setPhotos] = React.useState<Photo[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [description, setDescription] = React.useState('');
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [lightboxPhoto, setLightboxPhoto] = React.useState<Photo | null>(null);
    const [dragOver, setDragOver] = React.useState(false);
    const [dragIdx, setDragIdx] = React.useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [editDesc, setEditDesc] = React.useState('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchPhotos = React.useCallback(() => {
        setLoading(true);
        UserFetchJson(`/api/devices/${serialNumber}/photos`)
            .then((data: Photo[]) => {
                setPhotos(data);
                onCountChange?.(data.length);
            })
            .catch(() => setPhotos([]))
            .finally(() => setLoading(false));
    }, [serialNumber, onCountChange]);

    React.useEffect(() => {
        if (open) fetchPhotos();
    }, [open, fetchPhotos]);

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);

        try {
            const resized = await resizeImage(selectedFile);
            const formData = new FormData();
            formData.append('photo', resized, selectedFile.name);
            if (description.trim()) formData.append('description', description.trim());

            await UserFetch(`/api/devices/${serialNumber}/photos`, {
                method: 'POST',
                body: formData
            });

            setSelectedFile(null);
            setDescription('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchPhotos();
        } catch (e) {
            console.error('Upload failed:', e);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = (photoId: number) => {
        if (!window.confirm('Delete this photo?')) return;
        UserFetch(`/api/devices/${serialNumber}/photos/${photoId}`, { method: 'DELETE' })
            .then(() => fetchPhotos());
    };

    const handleFileDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) setSelectedFile(file);
    };

    const handleDescriptionSave = (photoId: number) => {
        const newDesc = editDesc.trim();
        const photo = photos.find(p => p.id === photoId);
        if (photo && newDesc !== (photo.description || '')) {
            UserFetch(`/api/devices/${serialNumber}/photos/${photoId}`, {
                method: 'PATCH',
                body: JSON.stringify({ description: newDesc }),
                headers: { 'Content-Type': 'application/json; charset=UTF-8' }
            }).then(() => {
                setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, description: newDesc || null } : p));
            });
        }
        setEditingId(null);
    };

    // Photo reorder via drag-and-drop
    const handlePhotoDragStart = (idx: number) => setDragIdx(idx);
    const handlePhotoDragOver = (e: React.DragEvent, idx: number) => {
        e.preventDefault();
        setDragOverIdx(idx);
    };
    const handlePhotoDragEnd = () => {
        if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
            const reordered = [...photos];
            const [moved] = reordered.splice(dragIdx, 1);
            reordered.splice(dragOverIdx, 0, moved);
            setPhotos(reordered);

            const order = reordered.map(p => p.id);
            UserFetch(`/api/devices/${serialNumber}/photos/order`, {
                method: 'PATCH',
                body: JSON.stringify({ order }),
                headers: { 'Content-Type': 'application/json; charset=UTF-8' }
            });
        }
        setDragIdx(null);
        setDragOverIdx(null);
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}
                PaperProps={{ sx: { minHeight: '50vh' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <Typography variant="h6" component="span">Photos — {deviceLabel || serialNumber}</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {/* Photo grid */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : photos.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No photos yet</Typography>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            gap: 1.5,
                        }}>
                            {photos.map((photo, idx) => (
                                <Box key={photo.id}
                                    draggable
                                    onDragStart={() => handlePhotoDragStart(idx)}
                                    onDragOver={(e) => handlePhotoDragOver(e, idx)}
                                    onDragEnd={handlePhotoDragEnd}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: dragOverIdx === idx ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        bgcolor: 'background.paper',
                                        opacity: dragIdx === idx ? 0.5 : 1,
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' },
                                        transition: 'border-color 0.15s',
                                    }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <img
                                            src={photo.url}
                                            alt={photo.filename}
                                            onClick={() => setLightboxPhoto(photo)}
                                            style={{
                                                width: '100%',
                                                height: 180,
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                display: 'block',
                                            }}
                                        />
                                        {isSuperAdmin && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(photo.id)}
                                                sx={{
                                                    position: 'absolute', top: 4, right: 4,
                                                    bgcolor: 'rgba(0,0,0,0.6)',
                                                    color: 'error.light',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                                }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Box sx={{ p: 1 }}>
                                        {editingId === photo.id ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                value={editDesc}
                                                onChange={(e) => setEditDesc(e.target.value)}
                                                onBlur={() => handleDescriptionSave(photo.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleDescriptionSave(photo.id);
                                                    if (e.key === 'Escape') setEditingId(null);
                                                }}
                                                placeholder="Add description"
                                                sx={{ mb: 0.5 }}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingId(photo.id);
                                                    setEditDesc(photo.description || '');
                                                }}
                                                sx={{
                                                    color: photo.description ? 'text.primary' : 'text.disabled',
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    fontStyle: photo.description ? 'normal' : 'italic',
                                                }}>
                                                {photo.description || 'Add description'}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                            {photo.uploaded_by_name} · {formatDate(photo.created)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Upload area */}
                    <Box
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleFileDrop}
                        sx={{
                            border: '2px dashed',
                            borderColor: dragOver ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            p: 2,
                            textAlign: 'center',
                            bgcolor: dragOver ? 'rgba(139, 31, 43, 0.1)' : 'transparent',
                            transition: 'all 0.2s',
                        }}>
                        {selectedFile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2">{selectedFile.name}</Typography>
                                <TextField
                                    size="small"
                                    placeholder="Description (optional)"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    sx={{ width: '100%', maxWidth: 400 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleUpload}
                                        disabled={uploading}
                                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}>
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setDescription('');
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <CloudUploadIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" sx={{ color: 'text.disabled', mb: 1 }}>
                                    Drag & drop a photo here, or
                                </Typography>
                                <Button variant="outlined" size="small" component="label">
                                    Browse Files
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        {...(fullScreen ? { capture: 'environment' } : {})}
                                        hidden
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) setSelectedFile(file);
                                        }}
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {lightboxPhoto && (
                <PhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />
            )}
        </>
    );
}

export default PhotosDialog;